import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { AlertIcon, CheckIcon, CloseIcon } from '@/shared/ui/icons';
import { ToastContext, type Toast, type ToastContextValue, type ToastTone } from '@/shared/ui/toast/toast-context';

const AUTO_DISMISS_MS = 5_000;
const MAX_VISIBLE = 3;

const TONE_STYLES: Record<ToastTone, { container: string; icon: ReactNode }> = {
  success: { container: 'border-approved/25 bg-approved-soft text-approved', icon: <CheckIcon /> },
  error: { container: 'border-rejected/25 bg-rejected-soft text-rejected', icon: <AlertIcon /> },
  info: { container: 'border-brand-200 bg-brand-50 text-brand-700', icon: <AlertIcon /> },
};

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const notify = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setToasts(current => [...current, { id, tone, message }].slice(-MAX_VISIBLE));
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS),
      );
      return id;
    },
    [dismiss],
  );

  // Limpa os timers pendentes no unmount para não vazar timeout em teste/HMR.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* `aria-live="polite"` anuncia o resultado da ação sem roubar o foco. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
      >
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm animate-scale-in items-start gap-2.5 rounded-lg border px-3.5 py-3 shadow-elevated',
              TONE_STYLES[toast.tone].container,
            )}
          >
            <span className="mt-px shrink-0 [&>svg]:size-[1.125rem]" aria-hidden="true">
              {TONE_STYLES[toast.tone].icon}
            </span>
            <p className="flex-1 text-[0.8125rem] leading-snug font-medium">{toast.message}</p>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Fechar aviso"
              className="-mt-0.5 -mr-1 shrink-0 rounded p-1 opacity-60 transition-opacity hover:opacity-100 [&>svg]:size-4"
            >
              <CloseIcon />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
